// IPFS utility — fetch() only, no axios (avoids CORS preflight issues)
// Primary: dedicated Pinata gateway (moccasin-advanced-llama-426.mypinata.cloud)
// Fallbacks: ipfs.io → dweb.link → cf-ipfs.com

const GENESIS_METADATA_CID = "bafkreigjkory2f6qzaus3spkz3qawy3332rjg5ilb5wgxecpc5iqhnneie";
const GENESIS_IMAGE_CID    = "bafkreih5hfiiugwndlyax3ys5yr3b7phesq2fsck7jttg2t22pp7m5iffy";

// Dedicated Pinata gateway — injected at build time, falls back to public gateway
export const PINATA_GATEWAY: string =
  import.meta.env.VITE_PINATA_GATEWAY ?? "https://gateway.pinata.cloud/ipfs/";

// Pinata JWT for authenticated requests
const PINATA_JWT: string = import.meta.env.VITE_PINATA_JWT ?? "";

// Gateway list — dedicated gateway first (fastest, CORS ✓), then public fallbacks
export const GATEWAYS: string[] = [
  PINATA_GATEWAY,
  "https://ipfs.io/ipfs/",
  "https://dweb.link/ipfs/",
  "https://cf-ipfs.com/ipfs/",
];

export function extractCID(uri: string): string | null {
  if (!uri) return null;
  if (uri.startsWith("ipfs://")) return uri.slice(7);
  const m = uri.match(/\/ipfs\/([^/?#]+)/);
  return m ? m[1] : null;
}

export function cidToHttp(uri: string, gatewayIndex = 0): string {
  if (!uri) return "";
  const cid = extractCID(uri);
  const gw  = GATEWAYS[gatewayIndex] ?? GATEWAYS[0];
  return cid ? gw + cid : uri;
}

export interface NFTMetadata {
  name:        string;
  description: string;
  image:       string;  // always ipfs:// URI — NFTImage handles gateway retries
}

/** Add JWT Bearer header for requests to our dedicated Pinata gateway */
function headersFor(url: string): HeadersInit {
  const h: Record<string, string> = { Accept: "application/json" };
  if (PINATA_JWT && url.includes("mypinata.cloud")) {
    h["Authorization"] = `Bearer ${PINATA_JWT}`;
  }
  return h;
}

export async function fetchMetadata(tokenURI: string): Promise<NFTMetadata> {
  const fallback: NFTMetadata = {
    name:        "NFT (Metadata Unavailable)",
    description: "",
    image:       "",
  };

  const cid = extractCID(tokenURI);
  if (!cid) return fallback;

  // Genesis fast-path: hardcoded — zero network calls, instant load
  if (cid === GENESIS_METADATA_CID) {
    return {
      name:        "NFT Marketplace Genesis #1",
      description: "Genesis NFT for nftmarketplace.ca",
      image:       `https://moccasin-advanced-llama-426.mypinata.cloud/ipfs/${GENESIS_IMAGE_CID}`,
    };
  }

  // Try each gateway with native fetch()
  for (const gw of GATEWAYS) {
    const url = gw + cid;
    try {
      const res = await fetch(url, {
        signal:  AbortSignal.timeout(10_000),
        headers: headersFor(url),
      });
      if (!res.ok) continue;
      const meta = (await res.json()) as Record<string, string>;
      const rawImage = meta.image ?? "";
      const imgCid   = extractCID(rawImage);
      // Keep as ipfs:// so NFTImage can cycle gateways on <img> error
      const image    = imgCid ? `ipfs://${imgCid}` : rawImage;
      return {
        name:        meta.name        ?? fallback.name,
        description: meta.description ?? "",
        image,
      };
    } catch {
      // try next gateway
    }
  }

  return fallback;
}

/** Resolve an ipfs:// URI to a concrete HTTP URL */
export function resolveImage(uri: string, gatewayIndex = 0): string {
  if (!uri) return "";
  const cid = extractCID(uri);
  return cid ? (GATEWAYS[gatewayIndex] ?? GATEWAYS[0]) + cid : uri;
}

/**
 * Upload a file to Pinata and return the IPFS CID.
 * Uses JWT Bearer auth — works on the free plan.
 */
export async function uploadToPinata(file: File, name: string): Promise<string> {
  if (!PINATA_JWT) throw new Error("VITE_PINATA_JWT not set");

  const formData = new FormData();
  formData.append("file", file);
  formData.append("pinataMetadata", JSON.stringify({ name }));
  formData.append("pinataOptions",  JSON.stringify({ cidVersion: 1 }));

  const res = await fetch("https://api.pinata.cloud/pinning/pinFileToIPFS", {
    method:  "POST",
    headers: { Authorization: `Bearer ${PINATA_JWT}` },
    body:    formData,
  });

  if (!res.ok) {
    const err = await res.text();
    throw new Error(`Pinata upload failed: ${err}`);
  }

  const data = (await res.json()) as { IpfsHash: string };
  return data.IpfsHash;
}
